import { DeleteIcon } from '@chakra-ui/icons';
import { IconButton, useToast } from '@chakra-ui/react';
import { deleteEmployee } from '../../hooks/employees';
import { useNavigate } from 'react-router-dom';

interface DeleteEmployeeButtonProps {
  orgId: string;
  employee: any;
  //   large: boolean;
  employeesCount: number;
  currentUserRole: string;
  onDeleted?: () => void; // callback to refresh list after deletion
  onClick?: (e: React.MouseEvent) => void; // new: to stop event propagation
}

export default function DeleteTeamMemberButton({
  orgId,
  employee,
  employeesCount,
  currentUserRole,
  onDeleted,
  onClick,
}: DeleteEmployeeButtonProps) {
  const toast = useToast();
  const navigate = useNavigate();

  const handleDelete = async (e: React.MouseEvent) => {
    // 1️⃣ Check number of employees
    e.stopPropagation(); // prevent card click
    if (onClick) onClick(e);

    // 2️⃣ Check access control
    if (currentUserRole !== 'admin' && currentUserRole !== 'manager') {
      toast({
        title: 'Access Denied',
        description: 'Only Admins or Managers can delete employees.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    if (employeesCount <= 1) {
      toast({
        title: 'Deletion Not Allowed',
        description: 'You cannot delete the only employee in the organization.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    // 3️⃣ Confirm delete
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${employee?.user?.first_name}?`,
    );
    if (!confirmDelete) return;

    // 4️⃣ Proceed with delete API
    const response = await deleteEmployee(orgId, employee.id);
    if (response.success) {
      toast({
        title: 'Employee Deleted',
        description: `${employee?.user?.first_name} was successfully removed.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      if (onDeleted) onDeleted();
      navigate(`/organization/${orgId}/team`);
    } else {
      toast({
        title: 'Deletion Failed',
        description: 'An error occurred while deleting this employee.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    }
  };

  return (
    <IconButton
      aria-label="Delete Team Member"
      icon={<DeleteIcon />}
      size="sm"
      colorScheme="red"
      variant="outline"
      onClick={handleDelete}
      _hover={{ bg: 'red.50' }}
    />
  );
}
